// client/src/pages/admin/AdminMoodAnalysisPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import adminService from '../../services/adminService';
import moodAnalyticsService from '../../services/moodAnalyticsService';
import { getMoodEmoji } from '../../utils/moodUtils';
import '../../styles/components/admin/AdminMoodAnalysisPage.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AdminMoodAnalysisPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const patientData = location.state?.patientData || {};
  const tenantId = location.state?.tenantId || null;

  const [moodHistory, setMoodHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [processedData, setProcessedData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [clinicalSummary, setClinicalSummary] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [rerunningAnalysis, setRerunningAnalysis] = useState({});

  // Fetch patient mood data using admin service
  useEffect(() => {
    if (patientId) {
      fetchPatientMoodData();
    }
  }, [patientId, timeRange, selectedDate]);

  const fetchPatientMoodData = async () => {
    if (!patientId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('🔍 ADMIN: Fetching mood data for patient:', patientId);
      
      // Use admin service to get mood data (searches across all tenants)
      const response = await adminService.getIndividualPatientMoodAnalytics(patientId, 30, tenantId);
      
      if (response.success && response.data) {
        console.log('✅ ADMIN: Mood data received:', response.data);
        
        // Extract mood history from the analytics data
        const moodEntries = response.data.moodHistory || [];
        setMoodHistory(moodEntries);
        setClinicalSummary(response.clinicalSummary || null);
        processDataByTimeRange(moodEntries, timeRange);
        
        // Filter recent entries (last 6 hours)
        const sixHoursAgo = new Date();
        sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
        const recent = moodEntries.filter(entry => 
          new Date(entry.timestamp) >= sixHoursAgo
        );
        setRecentEntries(recent);
      } else {
        console.log('📝 ADMIN: No mood data found');
        setError('No mood data found for this patient');
      }
    } catch (err) {
      console.error('❌ ADMIN: Error fetching mood data:', err);
      setError('Error loading patient mood data');
    } finally {
      setIsLoading(false);
    }
  };

  const processDataByTimeRange = (data, range) => {
    console.log('Processing data for range:', range, 'with', data.length, 'entries');
    
    if (!data || data.length === 0) {
      setProcessedData({
        entries: [],
        hourlyData: {},
        dailyData: {},
        weeklyData: {},
        moodCounts: {},
        sentimentData: { positive: 0, neutral: 0, negative: 0 },
        averageSentimentScore: 0,
        totalEntries: 0,
        dateLabel: 'No Data'
      });
      return;
    }

    switch (range) {
      case 'daily':
        processDailyData(data);
        break;
      case 'weekly':
        processWeeklyData(data);
        break;
      case 'monthly':
        processMonthlyData(data);
        break;
      default:
        processDailyData(data);
    }
  };

  const processDailyData = (data) => {
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);

    const dayEntries = data.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      return entryDate >= startOfDay && entryDate <= endOfDay;
    });

    // Group by 2-hour intervals
    const hourlyData = {};
    for (let hour = 0; hour < 24; hour += 2) {
      hourlyData[hour] = [];
    }

    dayEntries.forEach(entry => {
      const hour = new Date(entry.timestamp).getHours();
      const interval = Math.floor(hour / 2) * 2;
      hourlyData[interval].push(entry);
    });

    // Calculate metrics
    const moodCounts = {};
    const sentimentData = { positive: 0, neutral: 0, negative: 0 };
    let totalSentimentScore = 0;
    let sentimentCount = 0;

    dayEntries.forEach(entry => {
      moodCounts[entry.moodKey] = (moodCounts[entry.moodKey] || 0) + 1;
      
      if (entry.sentiment) {
        sentimentData[entry.sentiment.type]++;
        totalSentimentScore += entry.sentiment.score;
        sentimentCount++;
      }
    });

    setProcessedData({
      entries: dayEntries,
      hourlyData,
      moodCounts,
      sentimentData,
      averageSentimentScore: sentimentCount > 0 ? Math.round(totalSentimentScore / sentimentCount) : 0,
      totalEntries: dayEntries.length,
      dateLabel: startOfDay.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    });
  };

  const processWeeklyData = (data) => {
    const weekStart = new Date(selectedDate);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    weekStart.setHours(0, 0, 0, 0);
    
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const weekEntries = data.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      return entryDate >= weekStart && entryDate <= weekEnd;
    });

    // Group by day
    const dailyData = {};
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    days.forEach(day => {
      dailyData[day] = [];
    });

    weekEntries.forEach(entry => {
      const dayName = days[new Date(entry.timestamp).getDay()];
      dailyData[dayName].push(entry);
    });

    // Calculate metrics
    const moodCounts = {};
    const sentimentData = { positive: 0, neutral: 0, negative: 0 };
    let totalSentimentScore = 0;
    let sentimentCount = 0;

    weekEntries.forEach(entry => {
      moodCounts[entry.moodKey] = (moodCounts[entry.moodKey] || 0) + 1;
      
      if (entry.sentiment) {
        sentimentData[entry.sentiment.type]++;
        totalSentimentScore += entry.sentiment.score;
        sentimentCount++;
      }
    });

    setProcessedData({
      entries: weekEntries,
      dailyData,
      hourlyData: {},
      weeklyData: {},
      moodCounts,
      sentimentData,
      averageSentimentScore: sentimentCount > 0 ? Math.round(totalSentimentScore / sentimentCount) : 0,
      totalEntries: weekEntries.length,
      dateLabel: `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`
    });
  };

  const processMonthlyData = (data) => {
    const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);

    const monthEntries = data.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      return entryDate >= monthStart && entryDate <= monthEnd;
    });

    // Group by week
    const weeklyData = {};
    for (let i = 1; i <= 5; i++) {
      weeklyData[`Week ${i}`] = [];
    }

    monthEntries.forEach(entry => {
      const date = new Date(entry.timestamp);
      const weekNum = Math.ceil(date.getDate() / 7);
      if (weeklyData[`Week ${weekNum}`]) {
        weeklyData[`Week ${weekNum}`].push(entry);
      }
    });

    // Calculate metrics
    const moodCounts = {};
    const sentimentData = { positive: 0, neutral: 0, negative: 0 };
    let totalSentimentScore = 0;
    let sentimentCount = 0;

    monthEntries.forEach(entry => {
      moodCounts[entry.moodKey] = (moodCounts[entry.moodKey] || 0) + 1;
      
      if (entry.sentiment) {
        sentimentData[entry.sentiment.type]++;
        totalSentimentScore += entry.sentiment.score;
        sentimentCount++;
      }
    });

    setProcessedData({
      entries: monthEntries,
      weeklyData,
      hourlyData: {},
      dailyData: {},
      moodCounts,
      sentimentData,
      averageSentimentScore: sentimentCount > 0 ? Math.round(totalSentimentScore / sentimentCount) : 0,
      totalEntries: monthEntries.length,
      dateLabel: monthStart.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    });
  };

  const handlePreviousDate = () => {
    const newDate = new Date(selectedDate);
    if (timeRange === 'daily') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (timeRange === 'weekly') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setSelectedDate(newDate);
  };

  const handleNextDate = () => {
    const newDate = new Date(selectedDate);
    const today = new Date();
    
    if (timeRange === 'daily') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (timeRange === 'weekly') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    
    if (newDate > today) {
      return;
    }
    
    setSelectedDate(newDate);
  };

  const getMostFrequentMoods = () => {
    if (!processedData || !processedData.moodCounts) return [];
    
    return Object.entries(processedData.moodCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([mood, count]) => ({
        mood,
        count,
        percentage: Math.round((count / processedData.totalEntries) * 100)
      }));
  };

  const getRiskLevelBadge = (riskLevel) => {
    const colors = {
      POSITIVE: '#4CAF50',
      LOW: '#8BC34A',
      MILD: '#FFC107',
      MODERATE: '#FF9800',
      HIGH: '#F44336',
      CRISIS: '#D32F2F',
      UNKNOWN: '#9E9E9E'
    };

    const color = colors[riskLevel] || colors.UNKNOWN;
    
    return (
      <span 
        className="amoodapage-risk-badge"
        style={{ 
          backgroundColor: color,
          color: 'white',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}
      >
        {riskLevel}
      </span>
    );
  };

  const handleRerunAnalysis = async (entryId) => {
    try {
      console.log(`🔄 ADMIN UI: Re-running analysis for entry: ${entryId}`);
      
      setRerunningAnalysis(prev => ({ ...prev, [entryId]: true }));
      
      const result = await adminService.rerunSentimentAnalysisAdmin(entryId);
      
      console.log('✅ ADMIN UI: Reanalysis result:', result);
      
      if (result.success) {
        const changeInfo = result.data.changed.any 
          ? `Analysis updated: ${result.data.previousAnalysis?.type} → ${result.data.newAnalysis.type} (${result.data.newAnalysis.score})`
          : 'Analysis confirmed (no significant changes)';
          
        console.log(`✅ Re-analysis completed. ${changeInfo}`);
        
        await fetchPatientMoodData();
        
      } else {
        throw new Error(result.message || 'Reanalysis failed');
      }
      
    } catch (error) {
      console.error('❌ ADMIN UI: Reanalysis error:', error);
      
      const errorMessage = error.message.includes('No reflection text') 
        ? 'Cannot reanalyze: This mood entry has no reflection text'
        : `Reanalysis failed: ${error.message}`;
        
      alert(errorMessage);
      
    } finally {
      setRerunningAnalysis(prev => ({ ...prev, [entryId]: false }));
    }
  };

  const getSentimentChartData = () => {
    if (!processedData) return null;

    return {
      labels: ['Positive', 'Neutral', 'Negative'],
      datasets: [{
        data: [
          processedData.sentimentData.positive,
          processedData.sentimentData.neutral,
          processedData.sentimentData.negative
        ],
        backgroundColor: ['#4CAF50', '#FFC107', '#F44336'],
        borderColor: ['#45a049', '#FFB300', '#E53935'],
        borderWidth: 2
      }]
    };
  };

  const getMoodTrendChartData = () => {
    if (!processedData) return null;

    let labels = [];
    let data = [];

    if (timeRange === 'daily') {
      if (!processedData.hourlyData) return null;
      for (let hour = 0; hour < 24; hour += 2) {
        labels.push(`${hour}:00`);
        const entries = processedData.hourlyData[hour] || [];
        const avgRating = entries.length > 0
          ? entries.reduce((sum, e) => sum + e.moodRating, 0) / entries.length
          : null;
        data.push(avgRating);
      }
    } else if (timeRange === 'weekly') {
      if (!processedData.dailyData) return null;
      labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      labels.forEach(day => {
        const fullDay = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][labels.indexOf(day)];
        const entries = processedData.dailyData[fullDay] || [];
        const avgRating = entries.length > 0
          ? entries.reduce((sum, e) => sum + e.moodRating, 0) / entries.length
          : null;
        data.push(avgRating);
      });
    } else if (timeRange === 'monthly') {
      if (!processedData.weeklyData) return null;
      labels = Object.keys(processedData.weeklyData);
      labels.forEach(week => {
        const entries = processedData.weeklyData[week] || [];
        const avgRating = entries.length > 0
          ? entries.reduce((sum, e) => sum + e.moodRating, 0) / entries.length
          : null;
        data.push(avgRating);
      });
    }

    return {
      labels,
      datasets: [{
        label: 'Average Mood Rating',
        data,
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        spanGaps: true
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
          callback: function(value) {
            const labels = ['', 'Upset', 'Struggling', 'Okay', 'Good', 'Great'];
            return labels[value] || '';
          }
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 20,
          font: {
            size: 14
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((context.parsed / total) * 100);
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          }
        }
      }
    }
  };

  const spinnerStyle = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  if (!patientId) {
    return (
      <div className="amoodapage-mood-analysis-page">
        <div className="amoodapage-page-header">
          <button className="amoodapage-back-button" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1>Error</h1>
        </div>
        <div className="amoodapage-error-state">
          <p>Patient ID is not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="amoodapage-mood-analysis-page">
      <style>{spinnerStyle}</style>
      
      <div className="amoodapage-mood-analysis-header">
        <div className="amoodapage-mood-header-container">
          <button className="amoodapage-mood-back-button" onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          
         <div className="amoodapage-mood-patient-details">
          <h1 className="amoodapage-mood-patient-name">{patientData?.patientName || 'Patient'}</h1>
          <div className="amoodapage-mood-patient-info-row">
            <p className="amoodapage-mood-patient-email">
              <strong>Email:</strong> {patientData?.patientEmail || 'No email available'}
            </p>
            {tenantId && (
              <>
                <span className="amoodapage-mood-divider">|</span>
                <p className="amoodapage-mood-tenant-info">
                  <strong>Tenant:</strong> {tenantId}
                </p>
              </>
            )}
          </div>
        </div>
          
          <div className="amoodapage-mood-header-right">
            <span className="amoodapage-mood-active-badge">
              <span className="amoodapage-mood-status-dot"></span>
              Active Patient
            </span>
            <span className="amoodapage-mood-reg-date">
              Registration: {patientData?.registrationDate 
                ? new Date(patientData.registrationDate).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric', 
                    year: 'numeric'
                  })
                : 'Unknown'
              }
            </span>
          </div>
        </div>
      </div>

      <div className="amoodapage-page-controls">
        <div className="amoodapage-tab-navigation">
          <button 
            className={`amoodapage-tab-button ${activeTab === 'recent' ? 'active' : ''}`}
            onClick={() => setActiveTab('recent')}
          >
            Recent
            {recentEntries.length > 0 && (
              <span className="amoodapage-tab-badge">
                {recentEntries.length}
              </span>
            )}
          </button>
          <button 
            className={`amoodapage-tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`amoodapage-tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>
      </div>

      <div className="amoodapage-page-content">
        {isLoading && (
          <div className="amoodapage-loading-state">
            <div className="amoodapage-loading-spinner"></div>
            <p>Loading mood data...</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="amoodapage-error-state">
            <p>Error: {error}</p>
            <button onClick={fetchPatientMoodData}>Retry</button>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {activeTab === 'recent' && (
              <div className="amoodapage-recent-tab">
                <h3>Recent Mood Check-Ins (Last 6 Hours)</h3>
                
                {recentEntries.length > 0 ? (
                  <div className="amoodapage-recent-entries-list">
                    {recentEntries.map((entry, index) => (
                      <div key={entry._id || index} className="amoodapage-recent-entry-card">
                        <div className="amoodapage-entry-header">
                          <div className="amoodapage-entry-mood-info">
                            <img 
                              src={getMoodEmoji(entry.moodKey)} 
                              alt={entry.moodLabel}
                              className="amoodapage-mood-emoji"
                              style={{ width: '55px', height: '55px' }}
                            />
                            <div>
                              <div className="amoodapage-mood-label">{entry.moodLabel}</div>
                              <div className="amoodapage-entry-time">
                                {new Date(entry.timestamp).toLocaleTimeString('en-US', { 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  hour12: true 
                                })} - {new Date(entry.timestamp).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </div>
                            </div>
                          </div>
                          
                          <button
                            className="amoodapage-rerun-ai-button"
                            onClick={() => handleRerunAnalysis(entry._id)}
                            disabled={rerunningAnalysis[entry._id]}
                          >
                            {rerunningAnalysis[entry._id] ? (
                              <>
                                <span className="amoodapage-spinner">⟳</span>
                                Re-running...
                              </>
                            ) : (
                              <>
                                <span>🔄</span>
                                Re-run AI Analysis
                              </>
                            )}
                          </button>
                        </div>

                        {entry.reflection && (
                          <div className="amoodapage-reflection-box">
                            <div className="amoodapage-reflection-label">Patient Reflection:</div>
                            {entry.reflection}
                          </div>
                        )}

                        {entry.sentiment && (
                          <div className="amoodapage-sentiment-analysis-section">
                            <div className="amoodapage-sentiment-header">AI Sentiment Analysis:</div>
                            
                            <div className="amoodapage-sentiment-grid">
                              <div className="amoodapage-sentiment-item">
                                <span className="amoodapage-sentiment-label">Sentiment Type:</span>
                                <span className={`amoodapage-sentiment-type ${entry.sentiment.type}`}>
                                  {entry.sentiment.type}
                                </span>
                              </div>
                              
                              <div className="amoodapage-sentiment-item">
                                <span className="amoodapage-sentiment-label">Confidence Score:</span>
                                <span className="amoodapage-sentiment-value">{entry.sentiment.score}%</span>
                              </div>
                              
                              <div className="amoodapage-sentiment-item">
                                <span className="amoodapage-sentiment-label">Risk Level:</span>
                                {getRiskLevelBadge(entry.sentiment.riskLevel)}
                              </div>
                              
                              {entry.sentiment.phq9Analysis && (
                                <div className="amoodapage-sentiment-item">
                                  <span className="amoodapage-sentiment-label">PHQ-9 Symptoms:</span>
                                  <span className="amoodapage-sentiment-value">
                                    {entry.sentiment.phq9Analysis.symptomCount} detected
                                  </span>
                                </div>
                              )}
                              
                              {entry.sentiment.moodValidation && (
                                <div className="amoodapage-sentiment-item">
                                  <span className="amoodapage-sentiment-label">Mood Validation:</span>
                                  <span className={`amoodapage-mood-validation ${entry.sentiment.moodValidation.status}`}>
                                    {entry.sentiment.moodValidation.status}
                                  </span>
                                </div>
                              )}
                              
                              {entry.sentiment.clinicalNotes && (
                                <div className="amoodapage-clinical-notes">
                                  <div className="amoodapage-notes-label">Clinical Notes:</div>
                                  <div className="amoodapage-notes-content">
                                    {entry.sentiment.clinicalNotes}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="amoodapage-empty-state">
                    <div className="amoodapage-empty-icon">🕐</div>
                    <p className="amoodapage-empty-title">No recent mood check-ins in the last 6 hours</p>
                    <p className="amoodapage-empty-subtitle">Recent entries will appear here as patient logs their moods</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'overview' && processedData && (
              <div className="amoodapage-overview-tab">
                <div className="amoodapage-overview-controls">
                  <div className="amoodapage-time-range-selector">
                    <label>View:</label>
                    <select 
                      value={timeRange} 
                      onChange={(e) => {
                        setError(null);
                        setTimeRange(e.target.value);
                      }}
                      className="amoodapage-time-range-dropdown"
                      disabled={isLoading}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  
                  <div className="amoodapage-date-selector">
                    <button 
                      className="amoodapage-nav-button"
                      onClick={handlePreviousDate}
                      disabled={isLoading}
                    >
                      ←
                    </button>
                    <span className="amoodapage-date-display">
                      {processedData?.dateLabel || selectedDate.toLocaleDateString()}
                    </span>
                    <button 
                      className="amoodapage-nav-button"
                      onClick={handleNextDate}
                      disabled={isLoading || selectedDate >= new Date()}
                    >
                      →
                    </button>
                  </div>
                </div>

                {processedData.entries.length > 0 ? (
                  <div className="amoodapage-analytics-content">
                    <div className="amoodapage-summary-section">
                      <h3>Sentiment Summary: <strong>{processedData.dateLabel}</strong></h3>
                      <div className="amoodapage-summary-cards">
                        <div className="amoodapage-summary-card amoodapage-positive">
                          <div className="amoodapage-card-label">Positive</div>
                          <div className="amoodapage-card-value">{processedData.sentimentData.positive}</div>
                          <div className="amoodapage-card-detail">/ {processedData.totalEntries} check-ins</div>
                        </div>
                        <div className="amoodapage-summary-card amoodapage-neutral">
                          <div className="amoodapage-card-label">Neutral</div>
                          <div className="amoodapage-card-value">{processedData.sentimentData.neutral}</div>
                          <div className="amoodapage-card-detail">/ {processedData.totalEntries} check-ins</div>
                        </div>
                        <div className="amoodapage-summary-card amoodapage-negative">
                          <div className="amoodapage-card-label">Negative</div>
                          <div className="amoodapage-card-value">{processedData.sentimentData.negative}</div>
                          <div className="amoodapage-card-detail">/ {processedData.totalEntries} check-ins</div>
                        </div>
                        <div className="amoodapage-summary-card amoodapage-unlogged">
                          <div className="amoodapage-card-label">Unlogged</div>
                          <div className="amoodapage-card-value">
                            {timeRange === 'daily' ? Math.max(0, 12 - processedData.totalEntries) : 0}
                          </div>
                          <div className="amoodapage-card-detail">/ {timeRange === 'daily' ? '12 check-ins' : 'N/A'}</div>
                        </div>
                      </div>
                    </div>

                    <div className="amoodapage-score-summary-section">
                      <div className="amoodapage-score-card">
                        <h4>Overall Sentiment Score</h4>
                        <div className="amoodapage-score-value">{processedData.averageSentimentScore}%</div>
                        <div className="amoodapage-score-summary">
                          {processedData.averageSentimentScore >= 70 ? (
                            <p>{patientData?.patientName} has been feeling mostly positive during this period.</p>
                          ) : processedData.averageSentimentScore >= 40 ? (
                            <p>{patientData?.patientName} has had mixed emotions during this period.</p>
                          ) : (
                            <p>{patientData?.patientName} has been struggling emotionally during this period.</p>
                          )}
                        </div>
                      </div>

                      <div className="amoodapage-mood-frequency-card">
                        <h4>Most Frequent Moods</h4>
                        <div className="amoodapage-mood-list">
                          <div className="amoodapage-mood-frequency-card-divider"></div>
                          {getMostFrequentMoods().map((item, index) => (
                            <div key={index} className="amoodapage-mood-item">
                              <img 
                                src={getMoodEmoji(item.mood)} 
                                alt={item.mood}
                                className="amoodapage-mood-emoji"
                                style={{ width: '29px', height: '29px' }}
                              />
                              <span className="amoodapage-mood-name">I'm {item.mood}</span>
                              <span className="amoodapage-mood-count">{item.count} times ({item.percentage}%)</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="amoodapage-charts-section">
                      <div className="amoodapage-chart-container">
                        <h4>Mood Trend Over Time</h4>
                        <div className="amoodapage-chart-wrapper">
                          {getMoodTrendChartData() ? (
                            <Line data={getMoodTrendChartData()} options={chartOptions} />
                          ) : (
                            <div className="amoodapage-no-chart-data">
                              <p>No mood data available for this time period</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="amoodapage-chart-container">
                        <h4>Sentiment Distribution</h4>
                        <div className="amoodapage-chart-wrapper">
                          {getSentimentChartData() ? (
                            <Doughnut data={getSentimentChartData()} options={doughnutOptions} />
                          ) : (
                            <div className="amoodapage-no-chart-data">
                              <p>No sentiment data available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {clinicalSummary && (
                      <div className="amoodapage-clinical-summary-section">
                        <h4>Clinical Summary</h4>
                        <div className="amoodapage-clinical-summary-content">
                          <div className="amoodapage-summary-item">
                            <span className="amoodapage-summary-label">Risk Assessment:</span>
                            <span className="amoodapage-summary-value">{clinicalSummary.riskLevel || 'Not Available'}</span>
                          </div>
                          <div className="amoodapage-summary-item">
                            <span className="amoodapage-summary-label">Trend Analysis:</span>
                            <span className="amoodapage-summary-value">{clinicalSummary.trendAnalysis || 'Not Available'}</span>
                          </div>
                          {clinicalSummary.recommendations && (
                            <div className="amoodapage-summary-item">
                              <span className="amoodapage-summary-label">Recommendations:</span>
                              <span className="amoodapage-summary-value">{clinicalSummary.recommendations}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="amoodapage-empty-state">
                    <div className="amoodapage-empty-icon">📊</div>
                    <p className="amoodapage-empty-title">No mood data for {processedData.dateLabel}</p>
                    <p className="amoodapage-empty-subtitle">Try selecting a different date or time range</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
  <div className="amoodapage-history-tab">
    <h3>Mood Check-In History</h3>
    
    {moodHistory.length > 0 ? (
      <div className="amoodapage-recent-entries-list">
        {moodHistory.map((entry, index) => (
          <div key={entry._id || index} className="amoodapage-recent-entry-card">
            <div className="amoodapage-entry-header">
              <div className="amoodapage-entry-mood-info">
                <img 
                  src={getMoodEmoji(entry.moodKey)} 
                  alt={entry.moodLabel}
                  className="amoodapage-mood-emoji"
                  style={{ width: '55px', height: '55px' }}
                />
                <div>
                  <div className="amoodapage-mood-label">{entry.moodLabel}</div>
                  <div className="amoodapage-entry-time">
                    {new Date(entry.timestamp).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true 
                    })} - {new Date(entry.timestamp).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              </div>
              
              <button
                className="amoodapage-rerun-ai-button"
                onClick={() => handleRerunAnalysis(entry._id)}
                disabled={rerunningAnalysis[entry._id]}
              >
                {rerunningAnalysis[entry._id] ? (
                  <>
                    <span className="amoodapage-spinner">⟳</span>
                    Re-running...
                  </>
                ) : (
                  <>
                    <span>🔄</span>
                    Re-run AI Analysis
                  </>
                )}
              </button>
            </div>

            {entry.reflection && (
              <div className="amoodapage-reflection-box">
                <div className="amoodapage-reflection-label">Patient Reflection:</div>
                {entry.reflection}
              </div>
            )}

            {entry.sentiment && (
              <div className="amoodapage-sentiment-analysis-section">
                <div className="amoodapage-sentiment-header">AI Sentiment Analysis:</div>
                
                <div className="amoodapage-sentiment-grid">
                  <div className="amoodapage-sentiment-item">
                    <span className="amoodapage-sentiment-label">Sentiment Type:</span>
                    <span className={`amoodapage-sentiment-type ${entry.sentiment.type}`}>
                      {entry.sentiment.type}
                    </span>
                  </div>
                  
                  <div className="amoodapage-sentiment-item">
                    <span className="amoodapage-sentiment-label">Confidence Score:</span>
                    <span className="amoodapage-sentiment-value">{entry.sentiment.score}%</span>
                  </div>
                  
                  <div className="amoodapage-sentiment-item">
                    <span className="amoodapage-sentiment-label">Risk Level:</span>
                    {getRiskLevelBadge(entry.sentiment.riskLevel)}
                  </div>
                  
                  {entry.sentiment.phq9Analysis && (
                    <div className="amoodapage-sentiment-item">
                      <span className="amoodapage-sentiment-label">PHQ-9 Symptoms:</span>
                      <span className="amoodapage-sentiment-value">
                        {entry.sentiment.phq9Analysis.symptomCount} detected
                      </span>
                    </div>
                  )}
                  
                  {entry.sentiment.moodValidation && (
                    <div className="amoodapage-sentiment-item">
                      <span className="amoodapage-sentiment-label">Mood Validation:</span>
                      <span className={`amoodapage-mood-validation ${entry.sentiment.moodValidation.status}`}>
                        {entry.sentiment.moodValidation.status}
                      </span>
                    </div>
                  )}
                  
                  {entry.sentiment.clinicalNotes && (
                    <div className="amoodapage-clinical-notes">
                      <div className="amoodapage-notes-label">Clinical Notes:</div>
                      <div className="amoodapage-notes-content">
                        {entry.sentiment.clinicalNotes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    ) : (
      <div className="amoodapage-empty-state">
        <div className="amoodapage-empty-icon">📝</div>
        <p className="amoodapage-empty-title">No mood check-in history available</p>
        <p className="amoodapage-empty-subtitle">Patient mood entries will appear here once they start logging</p>
      </div>
    )}
  </div>
)}

            {activeTab === 'recommendations' && (
              <div className="amoodapage-recommendations-tab">
                <h3>Clinical Notes & Administrative Actions</h3>
                
                <div className="amoodapage-recommendations-content">
                  <div className="amoodapage-recommendation-section">
                    <h4>📋 Assessment Summary</h4>
                    <div className="amoodapage-assessment-grid">
                      <div className="amoodapage-assessment-item">
                        <span className="amoodapage-assessment-label">Total Check-ins:</span>
                        <span className="amoodapage-assessment-value">{moodHistory.length}</span>
                      </div>
                      <div className="amoodapage-assessment-item">
                        <span className="amoodapage-assessment-label">Average Sentiment:</span>
                        <span className="amoodapage-assessment-value">
                          {processedData?.averageSentimentScore || 0}%
                        </span>
                      </div>
                      <div className="amoodapage-assessment-item">
                        <span className="amoodapage-assessment-label">Dominant Mood:</span>
                        <span className="amoodapage-assessment-value">
                          {getMostFrequentMoods()[0]?.mood || 'N/A'}
                        </span>
                      </div>
                      <div className="amoodapage-assessment-item">
                        <span className="amoodapage-assessment-label">Patient Status:</span>
                        <span className="amoodapage-assessment-value">
                          {patientData?.accountStatus || 'Active'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="amoodapage-recommendation-section">
                    <h4>🎯 Administrative Recommendations</h4>
                    <div className="amoodapage-recommendation-list">
                      {processedData?.averageSentimentScore >= 70 ? (
                        <>
                          <div className="amoodapage-recommendation-item amoodapage-positive">
                            <div className="amoodapage-recommendation-icon">✅</div>
                            <div className="amoodapage-recommendation-content">
                              <div className="amoodapage-recommendation-title">Patient Progressing Well</div>
                              <div className="amoodapage-recommendation-description">
                                Patient shows consistent positive mood patterns. Continue monitoring current progress.
                              </div>
                            </div>
                          </div>
                          <div className="amoodapage-recommendation-item">
                            <div className="amoodapage-recommendation-icon">📅</div>
                            <div className="amoodapage-recommendation-content">
                              <div className="amoodapage-recommendation-title">Maintain Check-in Schedule</div>
                              <div className="amoodapage-recommendation-description">
                                Current monitoring frequency appears adequate. Schedule routine follow-ups.
                              </div>
                            </div>
                          </div>
                        </>
                      ) : processedData?.averageSentimentScore >= 40 ? (
                        <>
                          <div className="amoodapage-recommendation-item amoodapage-warning">
                            <div className="amoodapage-recommendation-icon">⚠️</div>
                            <div className="amoodapage-recommendation-content">
                              <div className="amoodapage-recommendation-title">Requires Administrative Attention</div>
                              <div className="amoodapage-recommendation-description">
                                Mixed emotional patterns detected. Consider flagging for clinical team review.
                              </div>
                            </div>
                          </div>
                          <div className="amoodapage-recommendation-item">
                            <div className="amoodapage-recommendation-icon">👨‍⚕️</div>
                            <div className="amoodapage-recommendation-content">
                              <div className="amoodapage-recommendation-title">Doctor Assignment Review</div>
                              <div className="amoodapage-recommendation-description">
                                Verify patient has assigned healthcare provider and adequate support.
                              </div>
                            </div>
                          </div>
                          <div className="amoodapage-recommendation-item">
                            <div className="amoodapage-recommendation-icon">📞</div>
                            <div className="amoodapage-recommendation-content">
                              <div className="amoodapage-recommendation-title">Increase Contact Frequency</div>
                              <div className="amoodapage-recommendation-description">
                                Consider increasing check-in reminders or wellness outreach.
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="amoodapage-recommendation-item amoodapage-urgent">
                            <div className="amoodapage-recommendation-icon">🚨</div>
                            <div className="amoodapage-recommendation-content">
                              <div className="amoodapage-recommendation-title">Immediate Administrative Action Required</div>
                              <div className="amoodapage-recommendation-description">
                                Concerning mood patterns detected. Flag for immediate clinical review and intervention.
                              </div>
                            </div>
                          </div>
                          <div className="amoodapage-recommendation-item">
                            <div className="amoodapage-recommendation-icon">📋</div>
                            <div className="amoodapage-recommendation-content">
                              <div className="amoodapage-recommendation-title">Crisis Protocol Review</div>
                              <div className="amoodapage-recommendation-description">
                                Review patient emergency contacts and ensure crisis intervention protocols are current.
                              </div>
                            </div>
                          </div>
                          <div className="amoodapage-recommendation-item">
                            <div className="amoodapage-recommendation-icon">👥</div>
                            <div className="amoodapage-recommendation-content">
                              <div className="amoodapage-recommendation-title">Care Team Notification</div>
                              <div className="amoodapage-recommendation-description">
                                Immediately notify assigned healthcare providers and consider priority scheduling.
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="amoodapage-recommendation-section">
                    <h4>📈 Administrative Monitoring Tasks</h4>
                    <div className="amoodapage-monitoring-list">
                      <div className="amoodapage-monitoring-item">
                        <span className="amoodapage-monitoring-icon">📱</span>
                        <span className="amoodapage-monitoring-text">Monitor daily mood check-in compliance</span>
                      </div>
                      <div className="amoodapage-monitoring-item">
                        <span className="amoodapage-monitoring-icon">📊</span>
                        <span className="amoodapage-monitoring-text">Review sentiment trend reports weekly</span>
                      </div>
                      <div className="amoodapage-monitoring-item">
                        <span className="amoodapage-monitoring-icon">🔄</span>
                        <span className="amoodapage-monitoring-text">Re-run AI analysis for flagged entries</span>
                      </div>
                      <div className="amoodapage-monitoring-item">
                        <span className="amoodapage-monitoring-icon">📋</span>
                        <span className="amoodapage-monitoring-text">Document significant changes in patient file</span>
                      </div>
                      <div className="amoodapage-monitoring-item">
                        <span className="amoodapage-monitoring-icon">👨‍⚕️</span>
                        <span className="amoodapage-monitoring-text">Ensure appropriate doctor-patient assignments</span>
                      </div>
                    </div>
                  </div>

                  <div className="amoodapage-recommendation-section">
                    <h4>⚠️ Administrative Red Flags</h4>
                    <div className="amoodapage-red-flags-list">
                      <div className="amoodapage-red-flag-item">
                        <span className="amoodapage-flag-icon">🔴</span>
                        <span className="amoodapage-flag-text">Patient stops logging mood check-ins entirely</span>
                      </div>
                      <div className="amoodapage-red-flag-item">
                        <span className="amoodapage-flag-icon">🔴</span>
                        <span className="amoodapage-flag-text">AI detects crisis-level risk classifications</span>
                      </div>
                      <div className="amoodapage-red-flag-item">
                        <span className="amoodapage-flag-icon">🔴</span>
                        <span className="amoodapage-flag-text">Multiple concerning sentiment patterns</span>
                      </div>
                      <div className="amoodapage-red-flag-item">
                        <span className="amoodapage-flag-icon">🔴</span>
                        <span className="amoodapage-flag-text">Patient account shows unusual access patterns</span>
                      </div>
                      <div className="amoodapage-red-flag-item">
                        <span className="amoodapage-flag-icon">🔴</span>
                        <span className="amoodapage-flag-text">No assigned healthcare provider for high-risk patient</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminMoodAnalysisPage;